import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Home as HomeIcon, Store, Briefcase, ArrowLeft, ArrowRight, Check, MapPin,
  Calendar as CalendarIcon, Clock, User, Phone, Mail, Sparkles, Sun, Sunset, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { useCatalog } from "../contexts/CatalogContext";
import { useCustomer } from "../contexts/CustomerContext";
import { API } from "../lib/api";
const STORAGE_KEY = "repairro_booking_draft_v1";
const TOTAL = 8;

const SEGMENT_OPTIONS = [
  { id: "home", name: "Home", icon: HomeIcon, desc: "Family appliance service for your home." },
  { id: "shop", name: "Shop / Office", icon: Store, desc: "Commercial units, bulk service contracts." },
  { id: "professional", name: "Working Professional", icon: Briefcase, desc: "Evening & weekend slots, fast booking." },
];

const Stepper = ({ step }) => (
  <div className="mb-8" data-testid="booking-stepper">
    <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-2">
      <span>Step {step} of {TOTAL}</span>
      <span>{Math.round((step / TOTAL) * 100)}% complete</span>
    </div>
    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-[#1A3C6E] to-[#FF6B2B] transition-all duration-500"
        style={{ width: `${(step / TOTAL) * 100}%` }}
      />
    </div>
  </div>
);

const dateOptions = () => {
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const day = d.toLocaleDateString("en-IN", { weekday: "short" });
    const dnum = d.getDate();
    const month = d.toLocaleDateString("en-IN", { month: "short" });
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    out.push({
      iso,
      day,
      dnum,
      month,
      isWeekend,
      label: getDateLabel(i),
    });
  }
  return out;
};

const getDateLabel = (i) => {
  if (i === 0) return "Today";
  if (i === 1) return "Tomorrow";
  return null;
};

const slotIcon = (id) => {
  if (id === "morning") return Sun;
  if (id === "afternoon") return Sun;
  if (id === "evening") return Sunset;
  return Zap;
};

const dateBtnClass = (active, isWeekend) => {
  if (active) return "border-[#1A3C6E] bg-[#1A3C6E] text-white";
  if (isWeekend) return "border-[#FF6B2B]/40 bg-[#FFF1EA] text-slate-700 hover:border-[#FF6B2B]";
  return "border-slate-200 hover:border-slate-300 text-slate-700";
};

const slotBtnClass = (active, highlight) => {
  if (active) return "border-[#1A3C6E] bg-[#1A3C6E] text-white";
  if (highlight) return "border-[#FF6B2B] bg-[#FFF1EA] text-slate-900";
  return "border-slate-200 hover:border-slate-300 text-slate-700";
};

const slotIconClass = (active, highlight) => {
  if (active) return "text-white";
  if (highlight) return "text-[#FF6B2B]";
  return "text-slate-500";
};

export default function Booking() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const { customer, isAuthenticated } = useCustomer();

  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (err) {
      // Draft restore failed (corrupt JSON) — fall through to default state.
    }
    return {
      customer_type: "",
      category: "",
      service: "",
      brand: "",
      address: "",
      city: "lucknow",
      pincode: "",
      landmark: "",
      date: "",
      slot: "",
      name: "",
      phone: "",
      email: "",
      notes: "",
    };
  });

  // Preselect from URL params (only once on mount) — intentionally one-shot
  useEffect(() => {
    const cat = params.get("category");
    const svc = params.get("service");
    const seg = params.get("segment");
    setData((d) => ({
      ...d,
      ...(seg && !d.customer_type ? { customer_type: seg } : {}),
      ...(cat && !d.category ? { category: cat } : {}),
      ...(svc && !d.service ? { service: svc } : {}),
    }));
    // jump to first incomplete step
    setStep((s) => {
      if (!data.customer_type && !seg) return 1;
      if (!data.category && !cat) return 2;
      if (!data.service && !svc) return 3;
      return s;
    });
    // mount-only — params/data intentionally excluded
  }, [params]);

  const { getCategoryById, getServiceById, categories, SLOTS, CITIES } = useCatalog();
  const bookingCategories = useMemo(() => {
    const roCategory = categories.find((c) => c.id === "ro");
    if (!roCategory) return categories;
    return [roCategory, ...categories.filter((c) => c.id !== "ro")];
  }, [categories]);

  useEffect(() => {
    if (customer?.phone || customer?.name) {
      setData((d) => ({
        ...d,
        phone: d.phone || customer?.phone || "",
        name: d.name || customer?.name || "",
      }));
    }
  }, [customer?.phone, customer?.name]);

  // Auto-save draft (non-sensitive form preferences only — no PII beyond what user is about to submit)
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      // Storage quota / blocked — drafts simply won't persist, not fatal.
    }
  }, [data]);

  const category = useMemo(() => getCategoryById(data.category), [data.category]);
  const service = useMemo(() => getServiceById(data.category, data.service), [data.category, data.service]);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const canNext = () => {
    switch (step) {
      case 1: return !!data.customer_type;
      case 2: return !!data.category && data.category === "ro";
      case 3: return !!data.service;
      case 4: return !!data.brand;
      case 5: return data.address.trim().length > 5 && !!data.city;
      case 6: return !!data.date && !!data.slot;
      case 7: return data.name.trim().length > 1 && /^\d{10}$/.test(data.phone.replace(/\D/g, ""));
      default: return true;
    }
  };

  const next = () => {
    if (!canNext()) {
      toast.error("Please complete the highlighted fields to continue.");
      return;
    }
    setStep((s) => Math.min(s + 1, TOTAL));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        service_name: service?.name || "",
        price: service?.price || 0,
      };
      const res = await axios.post(`${API}/bookings`, payload);
      localStorage.removeItem(STORAGE_KEY);
      navigate(`/booking/success/${res.data.booking_id}`, { state: { booking: res.data } });
    } catch (err) {
      toast.error("Could not confirm booking. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="text-center mb-8">
        <span className="text-xs font-semibold text-[#FF6B2B] uppercase tracking-widest">Book a Service</span>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">Tell us what you need</h1>
        <p className="mt-2 text-slate-600 text-sm">Takes under 60 seconds. Pay only after the job is done.</p>
      </div>

      <Stepper step={step} />

      <div className="bg-white rounded-2xl border border-slate-200 card-shadow p-6 sm:p-8 animate-fade-up">
        {/* STEP 1: Customer Type */}
        {step === 1 && (
          <div data-testid="step-1">
            <h2 className="font-display font-bold text-xl text-slate-900">Who is this booking for?</h2>
            <p className="text-sm text-slate-500 mt-1">We tailor the experience based on your needs.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {SEGMENT_OPTIONS.map((s) => {
                const Icon = s.icon;
                const active = data.customer_type === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`segment-option-${s.id}`}
                    onClick={() => update({ customer_type: s.id })}
                    className={`text-left p-5 rounded-xl border-2 transition-all ${
                      active ? "border-[#1A3C6E] bg-[#1A3C6E]/5" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Icon className={`w-7 h-7 ${active ? "text-[#FF6B2B]" : "text-slate-700"}`} />
                    <div className="font-display font-bold text-slate-900 mt-3">{s.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2: Category */}
        {step === 2 && (
          <div data-testid="step-2">
            <h2 className="font-display font-bold text-xl text-slate-900">Which RO service do you need?</h2>
            <p className="text-sm text-slate-500 mt-1">We are currently focusing on RO services. More categories will be available soon.</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {bookingCategories.map((c) => {
                const Icon = c.icon;
                const active = data.category === c.id;
                const disabled = c.status === "soon";
                return (
                  <button
                    key={c.id}
                    data-testid={`booking-category-${c.id}`}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      update({ category: c.id, service: "", brand: "" });
                    }}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      active ? "border-[#1A3C6E] bg-[#1A3C6E]/5" : "border-slate-200 hover:border-slate-300"
                    } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.bg, color: c.color }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-display font-bold text-slate-900 mt-3 text-sm">{c.short}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{disabled ? "Coming soon" : `From ₹${c.startingPrice}`}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3: Service */}
        {step === 3 && category && (
          <div data-testid="step-3">
            <h2 className="font-display font-bold text-xl text-slate-900">Pick the {category.short.toLowerCase()} service</h2>
            <div className="mt-5 space-y-3">
              {category.services.map((s) => {
                const active = data.service === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`booking-service-${s.id}`}
                    onClick={() => update({ service: s.id })}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start justify-between gap-4 ${
                      active ? "border-[#1A3C6E] bg-[#1A3C6E]/5" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{s.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                      <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {s.time}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-lg text-[#1A3C6E]">
                        ₹{s.price}
                        {s.id.includes("amc") && <span className="text-xs font-medium text-slate-500">/yr</span>}
                      </div>
                      <div className="text-[10px] text-slate-500">Starting from</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 4: Brand */}
        {step === 4 && category && (
          <div data-testid="step-4">
            <h2 className="font-display font-bold text-xl text-slate-900">Which brand?</h2>
            <p className="text-sm text-slate-500 mt-1">Don't see your brand? Pick "Other".</p>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[...category.brands, "Other"].map((b) => {
                const active = data.brand === b;
                return (
                  <button
                    key={b}
                    data-testid={`booking-brand-${b.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`}
                    onClick={() => update({ brand: b })}
                    className={`px-4 py-3 rounded-xl border-2 font-display font-semibold transition-all ${
                      active ? "border-[#1A3C6E] bg-[#1A3C6E] text-white" : "border-slate-200 text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {b}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 5: Address */}
        {step === 5 && (
          <div data-testid="step-5">
            <h2 className="font-display font-bold text-xl text-slate-900">Service address</h2>
            <p className="text-sm text-slate-500 mt-1">Where should our technician visit?</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">City *</label>
                <select
                  data-testid="booking-city"
                  value={data.city}
                  onChange={(e) => update({ city: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none bg-white"
                >
                  {CITIES.map((c) => (
                    <option key={c.id} value={c.id} disabled={c.status === "soon"}>
                      {c.name}{c.status === "soon" ? " (Coming soon)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Full address *</label>
                <textarea
                  data-testid="booking-address"
                  rows={3}
                  placeholder="House / Flat no, Building, Street, Area"
                  value={data.address}
                  onChange={(e) => update({ address: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Pincode</label>
                  <input
                    data-testid="booking-pincode"
                    value={data.pincode}
                    onChange={(e) => update({ pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Landmark</label>
                  <input
                    data-testid="booking-landmark"
                    placeholder="Near..."
                    value={data.landmark}
                    onChange={(e) => update({ landmark: e.target.value })}
                    className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 6: Date & Slot */}
        {step === 6 && (
          <div data-testid="step-6">
            <h2 className="font-display font-bold text-xl text-slate-900">Pick a date & time slot</h2>
            <p className="text-sm text-slate-500 mt-1">Evening slots and same-day urgent service highlighted in orange.</p>

            <div className="mt-5">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" /> Date
              </label>
              <div className="mt-2 grid grid-cols-4 sm:grid-cols-7 gap-2">
                {dateOptions().map((d) => {
                  const active = data.date === d.iso;
                  return (
                    <button
                      key={d.iso}
                      data-testid={`booking-date-${d.iso}`}
                      onClick={() => update({ date: d.iso })}
                      className={`p-2 rounded-xl border-2 text-center transition-all ${dateBtnClass(active, d.isWeekend)}`}
                    >
                      <div className="text-[10px] font-semibold uppercase">{d.label || d.day}</div>
                      <div className="font-display font-bold text-lg">{d.dnum}</div>
                      <div className="text-[10px]">{d.month}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Slot
              </label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SLOTS.map((s) => {
                  const Icon = slotIcon(s.id);
                  const active = data.slot === s.id;
                  return (
                    <button
                      key={s.id}
                      data-testid={`booking-slot-${s.id}`}
                      onClick={() => update({ slot: s.id })}
                      className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${slotBtnClass(active, s.highlight)}`}
                    >
                      <Icon className={`w-5 h-5 ${slotIconClass(active, s.highlight)}`} />
                      <div>
                        <div className="font-semibold text-sm">{s.label}</div>
                        <div className={`text-xs ${active ? "text-slate-200" : "text-slate-500"}`}>{s.time}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* STEP 7: Contact */}
        {step === 7 && (
          <div data-testid="step-7">
            <h2 className="font-display font-bold text-xl text-slate-900">Your contact details</h2>
            <p className="text-sm text-slate-500 mt-1">So our technician can reach you on the day.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Full name *</label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    data-testid="booking-name"
                    value={data.name}
                    onChange={(e) => update({ name: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Phone (10-digit) *</label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  {customer?.phone ? (
                    <input
                      data-testid="booking-phone"
                      inputMode="numeric"
                      value={data.phone}
                      readOnly
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-100 text-slate-700 outline-none cursor-not-allowed"
                    />
                  ) : (
                    <input
                      data-testid="booking-phone"
                      inputMode="numeric"
                      value={data.phone}
                      onChange={(e) => update({ phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
                    />
                  )}
                </div>
                {customer?.phone && (
                  <p className="text-xs text-slate-500 mt-2">
                    Using your registered mobile number from login.
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email (optional)</label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    data-testid="booking-email"
                    value={data.email}
                    onChange={(e) => update({ email: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
                <textarea
                  data-testid="booking-notes"
                  rows={3}
                  placeholder="Anything specific our technician should know?"
                  value={data.notes}
                  onChange={(e) => update({ notes: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 8: Review */}
        {step === 8 && service && category && (
          <div data-testid="step-8">
            <h2 className="font-display font-bold text-xl text-slate-900">Review & confirm</h2>
            <p className="text-sm text-slate-500 mt-1">Booking is free. Pay after job completion.</p>

            <div className="mt-5 bg-slate-50 rounded-xl p-5 space-y-3 text-sm">
              <Row k="Service" v={`${service.name} (${category.short})`} />
              <Row k="Brand" v={data.brand} />
              <Row k="Customer type" v={SEGMENT_OPTIONS.find(s => s.id === data.customer_type)?.name} />
              <Row k="Date & slot" v={`${data.date} • ${SLOTS.find(s => s.id === data.slot)?.label}`} />
              <Row k="Address" v={`${data.address}, ${CITIES.find(c => c.id === data.city)?.name}${data.pincode ? " - " + data.pincode : ""}`} />
              <Row k="Contact" v={`${data.name} • ${data.phone}${data.email ? " • " + data.email : ""}`} />
            </div>

            <div className="mt-5 rounded-xl border-2 border-dashed border-[#FF6B2B]/40 bg-[#FFF1EA] p-5 flex justify-between items-center">
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase">Estimated charge</div>
                <div className="font-display font-extrabold text-3xl text-[#1A3C6E] mt-1">
                  ₹{service.price}{service.id.includes("amc") && <span className="text-base font-medium text-slate-500">/yr</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1">+ spares at MRP (if needed, with your approval)</div>
              </div>
              <Sparkles className="w-10 h-10 text-[#FF6B2B]" />
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex justify-between gap-3">
          <button
            data-testid="step-back-btn"
            onClick={back}
            disabled={step === 1}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < TOTAL ? (
            <button
              data-testid="step-next-btn"
              onClick={next}
              className="px-6 py-2.5 rounded-xl bg-[#1A3C6E] hover:bg-[#132D53] text-white font-semibold flex items-center gap-2"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="step-confirm-btn"
              onClick={submit}
              disabled={submitting}
              className="px-6 py-2.5 rounded-xl bg-[#FF6B2B] hover:bg-[#E65A20] text-white font-semibold flex items-center gap-2 disabled:opacity-60"
            >
              {submitting ? "Confirming..." : "Confirm Booking"} <Check className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-slate-500">{k}</span>
    <span className="font-semibold text-slate-900 text-right">{v}</span>
  </div>
);
